// The form → wire direction, which `criteriaLoad.ts` is the inverse of.
//
// ⚠️ THE TWO SHAPES ARE NOT THE SAME OBJECT, AND THE SERVER REJECTS THE FORM'S.
// `mapLocationCriteria` (`search.page.component.ng1.ts:1435`) exists upstream for exactly this
// reason: the form holds `regions` as a `Record<name, boolean>` and the DTO declares
// `ArrayList<ExtensionLocationDTO>`. Sending the form's object straight through answers
//
//     400 JSON type mismatch: Cannot deserialize value of type
//     `java.util.ArrayList<…$ExtensionLocationDTO>` from Object value
//     (through reference chain: ExtensionCriteriaDTO["location"]->…["regions"])
//
// — which is what Save did on every candidate alert, and what `getPrediction` answered on
// every LOAD of the edit screen, so the counter never filled either.
import type { LocationCriteria, LocationRegion } from './LocationSection.tsx'

/** `IExtensionLocation`. Every field is sent, including the nulls — upstream builds it so. */
export interface WireLocation {
  id?: string | number | null
  city?: string | null
  region_state?: string | null
  country?: string | null
  lat?: number | null
  lon?: number | null
  distance?: number | null
  unit?: string | null
}

/**
 * `IExtensionLocationCriteria` — the SAVE shape, which is not `SavedLocation`.
 *
 * `SavedLocation` describes what the server SENDS and is deliberately forgiving (a city may
 * arrive as `{ text }` or `{ city }`); this describes what it ACCEPTS, where every entry is a
 * full `IExtensionLocation` with explicit nulls. Keeping them as one type made the looser one
 * win and hid the mismatch that produced the 400.
 */
export interface WireLocationCriteria {
  country?: string
  regions?: WireLocation[]
  cities?: WireLocation[]
  globalCitiesOrRegions?: WireLocation[]
  distances?: WireLocation[]
  allowUnknown?: boolean
}

/** `getSelectedRegions` — the keys whose value is true, in insertion order. */
export function selectedRegionNames(criteria: LocationCriteria): string[] {
  return Object.keys(criteria.regions ?? {}).filter((name) => criteria.regions?.[name] === true)
}

/**
 * `hasAnyLocationDetailsInTempCriteria` — and when it is false the whole `location` key is
 * OMITTED, not sent empty. Upstream returns `undefined` from the mapper.
 *
 * ⚠️ `allowUnknown` ALONE IS NOT LOCATION DETAIL. It is appended after the check, so a form
 * where only that box is ticked still sends no location at all.
 */
export function hasLocationDetail(criteria: LocationCriteria): boolean {
  const cities = criteria.cities ?? []
  const global = criteria.globalCitiesOrRegions ?? []
  const distanceCities = criteria.distanceCities ?? []
  return (
    (criteria.country !== undefined && criteria.country !== '') ||
    (criteria.includeCities === true && cities.length > 0) ||
    selectedRegionNames(criteria).length > 0 ||
    (criteria.includeGlobalCitiesOrRegions === true && global.length > 0) ||
    (criteria.includeDistanceFrom === true && distanceCities.length > 0)
  )
}

/**
 * `getExtensionLocationObject` — the tag's own wire object where it has one.
 *
 * ⚠️ `lng` IS THE NAME THE TYPEAHEAD ANSWERS WITH AND `lon` IS THE NAME THE DTO WANTS;
 * upstream calls that "a small hack" and reads `location.lng || location.lon`. A mapper that
 * only reads `lon` drops the longitude of every city the user has just picked, and keeps it
 * for every city that was already saved — which is the kind of half-working that survives a
 * demo.
 */
export function wireLocationFromTag(source: Readonly<Record<string, unknown>> | undefined): WireLocation {
  const from = source ?? {}
  const num = (value: unknown): number | null => (typeof value === 'number' ? value : null)
  const str = (value: unknown): string | null => (typeof value === 'string' ? value : null)
  return {
    id: typeof from['id'] === 'string' || typeof from['id'] === 'number' ? (from['id'] as string | number) : null,
    city: str(from['city']),
    region_state: str(from['region_state']),
    country: str(from['country']),
    lat: num(from['lat']),
    lon: num(from['lng']) ?? num(from['lon']),
    distance: null,
    unit: null,
  }
}

/**
 * `mapLocationCriteria` — the form's location as the server's.
 *
 * `regions` is looked up against the country's region list so the full object (id, country)
 * can be sent; a name with no match is dropped rather than sent half-built, which is what
 * upstream's `find(...)` would throw on.
 */
export function mapLocation(
  criteria: LocationCriteria,
  regions: readonly LocationRegion[] = [],
): WireLocationCriteria | undefined {
  if (!hasLocationDetail(criteria)) return undefined

  const mapped: WireLocationCriteria = {}

  if (criteria.country !== undefined && criteria.country !== '') mapped.country = criteria.country

  const picked = selectedRegionNames(criteria)
  if (picked.length > 0) {
    const resolved = picked
      .map((name) => regions.find((region) => region.name.toLowerCase() === name.toLowerCase()))
      .filter((region): region is LocationRegion => region !== undefined)
      .map((region) => ({
        id: region.id ?? null,
        city: null,
        region_state: region.name,
        country: region.country ?? null,
        lat: null,
        lon: null,
        distance: null,
        unit: null,
      }))
    if (resolved.length > 0) mapped.regions = resolved
  }

  const cities = criteria.cities ?? []
  if (criteria.includeCities === true && cities.length > 0) {
    mapped.cities = cities.map((tag) => wireLocationFromTag(tag.source))
  }

  const global = criteria.globalCitiesOrRegions ?? []
  if (criteria.includeGlobalCitiesOrRegions === true && global.length > 0) {
    mapped.globalCitiesOrRegions = global.map((tag) => wireLocationFromTag(tag.source))
  }

  const distanceCities = criteria.distanceCities ?? []
  if (
    criteria.includeDistanceFrom === true &&
    criteria.distance !== undefined &&
    criteria.distance !== 0 &&
    distanceCities.length > 0
  ) {
    mapped.distances = distanceCities.map((tag) => ({
      ...wireLocationFromTag(tag.source),
      distance: criteria.distance ?? null,
      // "We currently only use kilometers" — upstream's comment and upstream's literal.
      unit: 'km',
    }))
  }

  // Appended AFTER the detail check, so it cannot by itself produce a location criterion.
  //
  // ⚠️ ASSIGNED THROUGH, NOT COERCED TO A BOOLEAN. Upstream writes
  // `locationCriteria.allowUnknown = tempCriteria.allowUnknown` (search.page.component.ng1.ts:1493),
  // so an untouched checkbox assigns `undefined` and `JSON.stringify` drops the key entirely.
  // Forcing `=== true` put `allowUnknown: false` on every single save; measured against
  // production build 354, which sent no such key on either the candidate or the contact form.
  if (criteria.allowUnknown !== undefined) mapped.allowUnknown = criteria.allowUnknown

  return mapped
}
